import React, {
  FC,
  ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import checkInViewIntersectionObserver from "utils/isInViewPortIntersectionObserver";
import PlaceIcon from "./PlaceIcon";

export interface NcImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  containerClassName?: string;
}

const NcImage: FC<NcImageProps> = ({
  containerClassName = "",
  alt = "nc-imgs",
  src = "",
  className = "object-cover w-full h-full",
  ...args
}) => {
  const _containerRef = useRef(null);

  const [__src, set__src] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // The item grid recycles this component, so a new src can land on an
    // instance that already loaded a different image. Drop the old one first,
    // otherwise we keep painting the previous item's picture until the new one
    // downloads - and indefinitely if it never does.
    set__src("");
    setImageLoaded(false);

    if (!_containerRef.current) return;

    let cancelled = false;
    let _imageEl: HTMLImageElement | null = null;

    const _handleImageLoaded = () => {
      if (cancelled) return;
      setImageLoaded(true);
      set__src(src);
    };

    const _imageOnViewPort = () => {
      if (cancelled) return;
      if (!src) {
        _handleImageLoaded();
        return;
      }
      _imageEl = new Image();
      _imageEl.src = src;
      _imageEl.addEventListener("load", _handleImageLoaded);
    };

    const observer = checkInViewIntersectionObserver({
      target: _containerRef.current as any,
      options: {
        root: null,
        rootMargin: "0%",
        threshold: 0,
      },
      freezeOnceVisible: true,
      callback: _imageOnViewPort,
    });

    return () => {
      // Stops a slow in-flight load from resolving onto a newer src
      cancelled = true;
      observer?.disconnect();
      _imageEl?.removeEventListener("load", _handleImageLoaded);
    };
  }, [src]);

  const renderLoadingPlaceholder = () => {
    return (
      <div
        data-testid="place-icon"
        className={`${className}  flex items-center justify-center bg-neutral-200 dark:bg-neutral-6000 text-neutral-100 dark:text-neutral-500`}
        onClick={args?.onClick}
      >
        <div className="h-2/4 max-w-[50%]">
          <PlaceIcon />
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-NcImage ${containerClassName}`}
      data-nc-id="NcImage"
      ref={_containerRef}
    >
      {__src && imageLoaded ? (
        <img
          src={__src}
          className={className}
          alt={alt}
          {...args}
          data-testid="NcImage"
        />
      ) : (
        renderLoadingPlaceholder()
      )}
    </div>
  );
};

export default NcImage;
